import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import {
  MeetingReportSearchResult,
  NoteSearchResultMap,
  SearchType,
  TranscriptSearchResult,
} from "@app/analytics/MeetingReport/types";
import {
  INITIAL_NOTE_SEARCH_RESULTS_MAP,
  getSearchResultsByParagraph,
  getSearchResultsByNoteTypeAndId,
} from "@app/analytics/MeetingReport/utils";
import type { TranscriptTurnParagraph } from "@app/common/types";

type SearchMode = "search" | "replace" | null;

interface SearchState {
  searchMode: SearchMode;
  setSearchMode: (mode: SearchMode) => void;
  // values
  activeSearchResultIndex: number;
  searchResults: MeetingReportSearchResult[];
  searchTerm: string;
  searchType: SearchType | null;
  searchResultsByParagraphId: Map<
    TranscriptTurnParagraph["id"],
    TranscriptSearchResult[]
  >;
  searchResultsByNoteTypeAndId: NoteSearchResultMap;

  // actions
  goToPreviousSearchResult: () => void;
  /** Circular pagination; will go back to the first result once at the end of all results */
  goToNextSearchResult: () => void;
  resetState: () => void;
  startSearch: (searchTerm: string, searchType: SearchType) => void;

  // setters
  setSearchResults: (searchResults: MeetingReportSearchResult[]) => void;
  setSearchTerm: (searchTerm: string) => void;
}

const DEFAULT_STATE = {
  searchMode: null,
  activeSearchResultIndex: 0,
  searchResults: [],
  searchResultsByParagraphId: new Map<
    TranscriptTurnParagraph["id"],
    TranscriptSearchResult[]
  >(),
  searchResultsByNoteTypeAndId: INITIAL_NOTE_SEARCH_RESULTS_MAP,
  searchTerm: "",
  searchType: null,
};

const useSearchStore = createWithEqualityFn<SearchState>(
  (set) => ({
    ...DEFAULT_STATE,
    resetState: () => set(DEFAULT_STATE),
    setSearchMode: (searchMode) => set({ searchMode }),
    goToPreviousSearchResult: () =>
      set((state) => ({
        activeSearchResultIndex: Math.max(0, state.activeSearchResultIndex - 1),
      })),
    goToNextSearchResult: () =>
      set((state) => {
        const nextIndex = state.activeSearchResultIndex + 1;
        if (nextIndex >= state.searchResults.length) {
          return { activeSearchResultIndex: 0 };
        }
        return { activeSearchResultIndex: nextIndex };
      }),
    setSearchResults: (searchResults) => {
      set({
        searchResults,
        searchResultsByParagraphId: getSearchResultsByParagraph(searchResults),
        searchResultsByNoteTypeAndId:
          getSearchResultsByNoteTypeAndId(searchResults),
      });
    },
    setSearchTerm: (searchTerm) => set({ searchTerm }),
    startSearch: (searchTerm, searchType) =>
      set({
        activeSearchResultIndex: 0,
        searchTerm,
        searchType,
      }),
  }),
  shallow
);

export default useSearchStore;
